"use client";

import { useState, useCallback, useEffect } from "react";
import { usePaymentInputs } from "react-payment-inputs";
import images from "react-payment-inputs/images";
import { CreditCard } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PhoneInput } from "@/components/ui/phone-input";
import { cn, formatCurrency } from "@/lib/utils";
import type {
  MoyasarCardFieldErrors,
  MoyasarTokenizeCardParams,
} from "@/lib/utils/moyasar";
import type { UseFormReturn } from "react-hook-form";
import type { Value } from "react-phone-number-input";
import { PaymentMethodsStrip } from "../ui/payment-methods-strip";
import { SarIcon } from "@/components/icons/SarIcon";

/** Pixel-perfect input: white, subtle border/shadow, rounded-lg, h-11, placeholder gray */
const inputClass =
  "h-11 w-full rounded-lg border border-input bg-white px-3 py-2.5 text-base text-gray-900 placeholder:text-gray-400 outline-none transition-[border-color,box-shadow] focus:border-primary focus:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)] aria-invalid:border-destructive aria-invalid:focus:shadow-destructive/20";

const LATIN_NAME_RE = /^[a-zA-Z\s'-]+$/;

function validateCardholderName(
  name: string,
  t: (key: string) => string,
): string | null {
  const trimmed = name.trim();
  if (!trimmed) return t("checkout.cardPayment.nameRequired");
  if (!LATIN_NAME_RE.test(trimmed)) {
    return t("checkout.cardPayment.nameEnglishOnly");
  }
  if (trimmed.split(/\s+/).filter(Boolean).length < 2) {
    return t("checkout.cardPayment.bothNamesRequired");
  }
  return null;
}

function mapMoyasarNameError(
  raw: string,
  t: (key: string) => string,
): string {
  const lower = raw.toLowerCase();
  if (
    lower.includes("english") ||
    lower.includes("alphabet") ||
    lower.includes("letter")
  ) {
    return t("checkout.cardPayment.nameEnglishOnly");
  }
  if (
    lower.includes("first") ||
    lower.includes("last") ||
    lower.includes("both") ||
    lower.includes("two") ||
    lower.includes("full")
  ) {
    return t("checkout.cardPayment.bothNamesRequired");
  }
  if (lower.includes("blank") || lower.includes("required")) {
    return t("checkout.cardPayment.nameRequired");
  }
  return t("checkout.cardPayment.bothNamesRequired");
}

export interface CustomCardFormProps {
  locale: string;
  t: (key: string) => string;
  total: number;
  onSubmit: (card: MoyasarTokenizeCardParams) => Promise<void>;
  isSubmitting?: boolean;
  error?: string | null;
  /** Per-field errors from Moyasar tokenization (shown under the matching input). */
  fieldErrors?: MoyasarCardFieldErrors | null;
  /** When true, show phone input above submit button (guest quick/specific project flow) */
  showPhoneWithCard?: boolean;
  /** Form with mobile field; required when showPhoneWithCard is true */
  form?: UseFormReturn<{ mobile: Value }>;
}

/**
 * Custom card payment form – pixel-perfect layout to match Figma:
 * Light blue dotted wrapper, header (title right + logos left), placeholder-only inputs,
 * CVC with icon, expiry with MM/YY, precise width ratios and spacing.
 */
export function CustomCardForm({
  locale,
  t,
  total,
  onSubmit,
  isSubmitting = false,
  error: externalError,
  fieldErrors: externalFieldErrors = null,
  showPhoneWithCard = false,
  form,
}: CustomCardFormProps) {
  const [cardholderName, setCardholderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvc, setCvc] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameTouched, setNameTouched] = useState(false);

  const {
    meta,
    getCardImageProps,
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
  } = usePaymentInputs({
    errorMessages: {
      emptyCardNumber: t("checkout.cardPayment.cardNumberRequired"),
      invalidCardNumber: t("checkout.cardPayment.invalidCardNumber"),
      emptyExpiryDate: t("checkout.cardPayment.expiryRequired"),
      monthOutOfRange: t("checkout.cardPayment.invalidExpiry"),
      yearOutOfRange: t("checkout.cardPayment.invalidExpiry"),
      dateOutOfRange: t("checkout.cardPayment.invalidExpiry"),
      invalidExpiryDate: t("checkout.cardPayment.invalidExpiry"),
      emptyCVC: t("checkout.cardPayment.cvcRequired"),
      invalidCVC: t("checkout.cardPayment.invalidCvc"),
    },
  });

  const isRtl = locale === "ar";

  useEffect(() => {
    if (externalFieldErrors?.name) {
      setNameError(mapMoyasarNameError(externalFieldErrors.name, t));
      setNameTouched(true);
    }
  }, [externalFieldErrors, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (meta.error || isSubmitting) return;

      setNameTouched(true);
      const localNameError = validateCardholderName(cardholderName, t);
      if (localNameError) {
        setNameError(localNameError);
        return;
      }
      setNameError(null);

      const number = cardNumber.replace(/\s/g, "");
      const [month, yearPart] = expiryDate
        .split("/")
        .map((s) => s?.trim() ?? "");
      const year =
        yearPart && yearPart.length === 2 ? `20${yearPart}` : yearPart || "";

      if (!number || !month || !year || !cvc) return;

      await onSubmit({
        name: cardholderName.trim(),
        number,
        month: month.padStart(2, "0"),
        year: year.length === 4 ? year.slice(-2) : year,
        cvc,
      });
    },
    [
      cardNumber,
      cardholderName,
      cvc,
      expiryDate,
      meta.error,
      isSubmitting,
      onSubmit,
      t,
    ],
  );

  const cardNumberProps = getCardNumberProps({
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setCardNumber(e.target.value),
  });
  const expiryProps = getExpiryDateProps({
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setExpiryDate(e.target.value),
  });
  const cvcProps = getCVCProps({
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setCvc(e.target.value),
  });

  const apiNumberError = externalFieldErrors?.number
    ? t("checkout.cardPayment.invalidCardNumber")
    : null;
  const apiExpiryError =
    externalFieldErrors?.month || externalFieldErrors?.year
      ? t("checkout.cardPayment.invalidExpiry")
      : null;
  const apiCvcError = externalFieldErrors?.cvc
    ? t("checkout.cardPayment.invalidCvc")
    : null;
  const hasFieldMappedError = Boolean(
    nameError || apiNumberError || apiExpiryError || apiCvcError,
  );
  const globalError =
    externalError && !hasFieldMappedError ? externalError : null;

  return (
    <form className="flex flex-col gap-6 " onSubmit={handleSubmit}>
      {/* Header: title (right) + payment logos (left), space-between */}
      <header className="flex flex-row items-center justify-between gap-1 md:gap-4">
        <h2 className="text-xs font-bold text-black sm:text-lg">
          {t("checkout.paymentTabCard")}
        </h2>
        <PaymentMethodsStrip
          items={["mada", "visa", "mastercard"]}
          size="sm"
          cardClassName="p-1"
        />
      </header>

      {/* 1) Cardholder name – full width, placeholder only, right-aligned */}
      <div className="flex flex-col gap-1">
        <Input
          id="cardholder-name"
          type="text"
          placeholder={t("checkout.cardPayment.cardholderName")}
          value={cardholderName}
          onChange={(e) => {
            setCardholderName(e.target.value);
            if (nameTouched) {
              setNameError(validateCardholderName(e.target.value, t));
            }
          }}
          onBlur={() => {
            setNameTouched(true);
            setNameError(validateCardholderName(cardholderName, t));
          }}
          className={cn(inputClass)}
          autoComplete="cc-name"
          aria-invalid={nameError ? true : undefined}
          aria-label={t("checkout.cardPayment.cardholderName")}
        />
        {nameError && (
          <p className="text-sm text-destructive">{nameError}</p>
        )}
      </div>

      {/* 2) Card number – full width, built-in detected card logo (react-payment-inputs) */}
      <div className="flex flex-col gap-1">
        <div className="relative">
          <span
            className={cn(
              "pointer-events-none absolute top-1/2 flex h-5 w-8  items-center justify-center rounded-md border border-input bg-slate-50 [&_svg]:h-5 [&_svg]:w-auto [&_svg]:object-contain",
              isRtl ? "right-3" : "left-3",
            )}
            aria-hidden
            style={{ transform: "translateY(-50%)" }}
          >
            {getCardImageProps && <svg {...getCardImageProps({ images })} />}
          </span>
          <Input
            id="card-number"
            {...cardNumberProps}
            value={cardNumber}
            className={cn(
              inputClass,
              "font-mono rtl:text-end  pl-15 rtl:pr-15 ",
            )}
            placeholder={t("checkout.cardPayment.cardNumber")}
            autoComplete="cc-number"
            aria-invalid={
              (meta.touchedInputs?.cardNumber &&
                meta.erroredInputs?.cardNumber) ||
              apiNumberError
                ? true
                : undefined
            }
            aria-label={t("checkout.cardPayment.cardNumber")}
          />
        </div>
        {(apiNumberError ||
          (meta.touchedInputs?.cardNumber &&
            meta.erroredInputs?.cardNumber)) && (
          <p className="text-sm text-destructive ">
            {apiNumberError || meta.erroredInputs?.cardNumber}
          </p>
        )}
      </div>

      {/* 3) Bottom row: CVC (~38%) + Expiry (~62%), clear gap */}
      <div className="grid grid-cols-[minmax(0,38%)_1fr] gap-4">
        {/* CVC – icon inside input left, placeholder "CVC" */}
        <div className="flex flex-col gap-1">
          <div className="relative">
            <span className="pointer-events-none absolute right-3 inset-y-0 flex items-center text-primary">
              <CreditCard className="h-5 w-5" aria-hidden />
            </span>
            <Input
              id="cvc"
              {...cvcProps}
              value={cvc}
              className={cn(inputClass, "rtl:pr-10 rtl:text-end")}
              placeholder="CVC"
              autoComplete="cc-csc"
              aria-invalid={
                (meta.touchedInputs?.cvc && meta.erroredInputs?.cvc) ||
                apiCvcError
                  ? true
                  : undefined
              }
              aria-label={t("checkout.cardPayment.cvc")}
            />
          </div>
          {(apiCvcError ||
            (meta.touchedInputs?.cvc && meta.erroredInputs?.cvc)) && (
            <p className="text-sm text-destructive">
              {apiCvcError || meta.erroredInputs?.cvc}
            </p>
          )}
        </div>
        {/* Expiry – placeholder 00/00, right-aligned */}
        <div className="flex flex-col gap-1">
          <Input
            id="expiry"
            {...expiryProps}
            value={expiryDate}
            className={cn(inputClass, "rtl:text-end")}
            placeholder="00/00"
            autoComplete="cc-exp"
            aria-invalid={
              (meta.touchedInputs?.expiryDate &&
                meta.erroredInputs?.expiryDate) ||
              apiExpiryError
                ? true
                : undefined
            }
            aria-label={t("checkout.cardPayment.expiryMonth")}
          />
          {(apiExpiryError ||
            (meta.touchedInputs?.expiryDate &&
              meta.erroredInputs?.expiryDate)) && (
            <p className="text-sm text-destructive ">
              {apiExpiryError || meta.erroredInputs?.expiryDate}
            </p>
          )}
        </div>
      </div>

      {globalError && (
        <p className="text-sm text-destructive">{globalError}</p>
      )}

      {showPhoneWithCard && form && (
        <Form {...form}>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Label className="text-sm font-semibold text-foreground">
                {t("common.mobile")} *
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("checkout.cardPayment.receiptSentToMobile")}
              </p>
            </div>
            <FormField
              control={form.control}
              name="mobile"
              render={({ field }) => (
                <FormItem className="space-y-2 ">
                  <FormControl>
                    <PhoneInput
                      placeholder="0000 0000 00"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                      defaultCountry="SA"
                      className={cn(
                        inputClass,
                        "focus-visible:ring-2 focus-visible:ring-primary/20 border-none",
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
      )}

      <Button
        type="submit"
        disabled={!!meta.error || isSubmitting}
        className="mt-2 h-12 w-full rounded-xl bg-success text-base font-bold text-success-foreground shadow-sm hover:bg-success/90 disabled:opacity-50"
      >
        {isSubmitting ? (
          <span className="inline-flex items-center gap-2">
            <span className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            {t("common.processing")}
          </span>
        ) : (
          <span className="inline-flex items-center justify-center gap-1.5">
            {t("checkout.completeDonation")} {formatCurrency(total, locale)}{" "}
            <SarIcon className="mx-1 shrink-0 text-white" />
          </span>
        )}
      </Button>
    </form>
  );
}
